import { useEffect, useState, useCallback, useMemo, type ReactNode } from 'react';
import { apiFetch } from '@/lib/api';
import { formatApiErrorBody } from '@/lib/formatApiError';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import {
  carryProductsListNavState,
  productsListPath,
  resolveProductsListSearch,
} from '@/lib/productsListNavigation';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { MarginCalculator } from '@/components/products';
import { ProductStatusBadge } from '@/components/products';
import { BarcodeScanInput } from '@/components/barcode';
import { deriveCasesPerPallet } from '@/lib/formatCaseUnits';
import { cn } from '@/lib/utils';
import { PAGE_GUTTERS, pageBleedXClass } from '@/components/global/PageContainer';
import { ArrowLeft, Save, Download } from 'lucide-react';

interface Category {
  id: string;
  name: string;
}

interface Supplier {
  id: string;
  name: string;
  code: string;
}

interface Brand {
  id: string;
  name: string;
}

type ProductStatus = 'ACTIVE' | 'DORMANT' | 'DELISTED' | 'UNMANAGED';

interface FormData {
  name: string;
  sku: string;
  unitBarcode: string;
  caseBarcode: string;
  supplierCode: string;
  categoryId: string;
  supplierId: string;
  brandId: string;
  status: ProductStatus;
  unitsPerCase: string;
  casePurchasePrice: string;
  unitWeight: string;
  caseWeight: string;
  casesPerLayer: string;
  layersPerPallet: string;
  purchasePrice: string;
  sellPrice: string;
  targetMarginPercent: string;
  reorderLeadDays: string;
}

const initialFormData: FormData = {
  name: '',
  sku: '',
  unitBarcode: '',
  caseBarcode: '',
  supplierCode: '',
  categoryId: 'none',
  supplierId: 'none',
  brandId: 'none',
  status: 'ACTIVE',
  unitsPerCase: '1',
  casePurchasePrice: '',
  unitWeight: '',
  caseWeight: '',
  casesPerLayer: '',
  layersPerPallet: '',
  purchasePrice: '',
  sellPrice: '',
  targetMarginPercent: '60',
  reorderLeadDays: '7',
};

function statusToIsActive(status: ProductStatus): boolean {
  return status !== 'DELISTED' && status !== 'UNMANAGED';
}

function roundToPence(value: number): string {
  return (Math.round(value * 100) / 100).toFixed(2);
}

function deriveUnitPurchasePrice(casePurchasePrice: string, unitsPerCase: string): string | null {
  const casePrice = parseFloat(casePurchasePrice);
  const units = parseInt(unitsPerCase, 10);
  if (!Number.isFinite(casePrice) || casePrice <= 0) return null;
  if (!Number.isFinite(units) || units < 1) return null;
  return roundToPence(casePrice / units);
}

function applyCasePackPricingUpdate(
  prev: FormData,
  field: 'unitsPerCase' | 'casePurchasePrice',
  value: string
): FormData {
  const next = { ...prev, [field]: value };
  const derived = deriveUnitPurchasePrice(next.casePurchasePrice, next.unitsPerCase);
  if (derived != null) {
    next.purchasePrice = derived;
  }
  return next;
}

function FormField({
  id,
  label,
  hint,
  required,
  children,
  className,
}: {
  id: string;
  label: string;
  hint?: string;
  required?: boolean;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('space-y-2', className)}>
      <Label htmlFor={id} className="text-sm font-medium">
        {label}
        {required && <span className="text-destructive ml-0.5">*</span>}
      </Label>
      {children}
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

function FormSection({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: ReactNode;
}) {
  return (
    <Card>
      <CardHeader className="pb-4">
        <CardTitle className="text-base font-semibold">{title}</CardTitle>
        {description && <CardDescription>{description}</CardDescription>}
      </CardHeader>
      <CardContent className="space-y-4">{children}</CardContent>
    </Card>
  );
}

export default function ProductFormPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const isEdit = Boolean(id);
  const productsListHref = productsListPath(resolveProductsListSearch(location));
  const productsListNav = carryProductsListNavState(location);

  function navigateBack() {
    if (isEdit && id) {
      navigate(`/inventory/products/${id}`, productsListNav);
      return;
    }
    navigate(productsListHref);
  }

  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [categories, setCategories] = useState<Category[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [unitsPerCaseError, setUnitsPerCaseError] = useState<string | null>(null);
  const [occaEnriching, setOccaEnriching] = useState(false);
  const [occaEnrichMessage, setOccaEnrichMessage] = useState<string | null>(null);

  const loadProduct = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch(`/api/v1/products/${id}`);
      if (!res.ok) throw new Error('Failed to load product');
      const data = await res.json();
      const status: ProductStatus =
        data.status || (data.isActive ? 'ACTIVE' : 'DELISTED');
      setFormData({
        name: data.name || '',
        sku: data.sku || '',
        unitBarcode: data.unitBarcode || data.barcode || '',
        caseBarcode: data.caseBarcode || '',
        supplierCode: data.supplierCode || '',
        categoryId: data.categoryId || 'none',
        supplierId: data.supplierId ? data.supplierId : 'none',
        brandId: data.brandId || 'none',
        status,
        unitsPerCase: String(data.unitsPerCase ?? 1),
        casePurchasePrice:
          data.casePurchasePrice != null ? String(data.casePurchasePrice) : '',
        unitWeight: data.unitWeight != null ? String(data.unitWeight) : '',
        caseWeight: data.caseWeight != null ? String(data.caseWeight) : '',
        casesPerLayer: data.casesPerLayer != null ? String(data.casesPerLayer) : '',
        layersPerPallet:
          data.layersPerPallet != null ? String(data.layersPerPallet) : '',
        purchasePrice: String(data.purchasePrice || ''),
        sellPrice: String(data.sellPrice || ''),
        targetMarginPercent: String(data.targetMarginPercent || '60'),
        reorderLeadDays: String(data.reorderLeadDays || '7'),
      });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load product');
    } finally {
      setLoading(false);
    }
  }, [id]);

  const loadCategories = useCallback(async () => {
    try {
      const res = await apiFetch('/api/v1/categories');
      if (res.ok) setCategories(await res.json());
    } catch (e) {
      console.error('Error loading categories:', e);
    }
  }, []);

  const loadSuppliers = useCallback(async () => {
    try {
      const res = await apiFetch('/api/v1/suppliers?pageSize=500');
      if (res.ok) {
        const data = await res.json();
        setSuppliers(data.items || []);
      }
    } catch (e) {
      console.error('Error loading suppliers:', e);
    }
  }, []);

  const loadBrands = useCallback(async () => {
    try {
      const res = await apiFetch('/api/v1/brands');
      if (res.ok) setBrands(await res.json());
    } catch (e) {
      console.error('Error loading brands:', e);
    }
  }, []);

  useEffect(() => {
    loadCategories();
    loadSuppliers();
    loadBrands();
    if (isEdit) loadProduct();
  }, [loadCategories, loadSuppliers, loadBrands, loadProduct, isEdit]);

  function updateField<K extends keyof FormData>(field: K, value: FormData[K]) {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (field === 'unitsPerCase') setUnitsPerCaseError(null);
  }

  const purchasePrice = useMemo(
    () => parseFloat(formData.purchasePrice) || 0,
    [formData.purchasePrice]
  );
  const sellPrice = useMemo(() => parseFloat(formData.sellPrice) || 0, [formData.sellPrice]);
  const targetMargin = useMemo(
    () => parseFloat(formData.targetMarginPercent) || 60,
    [formData.targetMarginPercent]
  );
  const palletCases = useMemo(
    () =>
      deriveCasesPerPallet(
        formData.casesPerLayer ? parseInt(formData.casesPerLayer, 10) : null,
        formData.layersPerPallet ? parseInt(formData.layersPerPallet, 10) : null
      ),
    [formData.casesPerLayer, formData.layersPerPallet]
  );

  const selectedSupplierName = useMemo(() => {
    if (formData.supplierId === 'none') return undefined;
    return suppliers.find((s) => s.id === formData.supplierId)?.name;
  }, [formData.supplierId, suppliers]);

  const showMarginPreview = purchasePrice > 0 && sellPrice > 0;
  const isUnmanaged = formData.status === 'UNMANAGED';

  async function handleEnrichFromOcca() {
    if (!id) return;
    if (!formData.unitBarcode.trim()) {
      setOccaEnrichMessage('Add a unit barcode first — Occa matching uses that barcode.');
      return;
    }

    setOccaEnriching(true);
    setOccaEnrichMessage(null);
    try {
      const res = await apiFetch(`/api/v1/products/${id}/enrich/occa`, { method: 'POST' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || 'Occa enrichment failed');
      }

      const applied = Array.isArray(data.result?.fieldsApplied) ? data.result.fieldsApplied : [];
      if (applied.length === 0) {
        setOccaEnrichMessage('Occa match found, but all target fields were already populated.');
      } else {
        setOccaEnrichMessage(
          `Updated from Occa (${data.result?.occaSku ?? 'matched SKU'}): ${applied.join(', ')}`
        );
      }
      await loadProduct();
    } catch (err: unknown) {
      setOccaEnrichMessage(err instanceof Error ? err.message : 'Occa enrichment failed');
    } finally {
      setOccaEnriching(false);
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    const unitsPerCase = parseInt(formData.unitsPerCase, 10);
    if (!Number.isFinite(unitsPerCase) || unitsPerCase < 1) {
      setUnitsPerCaseError('Units per case must be at least 1');
      return;
    }
    setUnitsPerCaseError(null);
    setSaving(true);

    try {
      const payload = {
        name: formData.name.trim(),
        sku: formData.sku.trim().toUpperCase(),
        unitBarcode: formData.unitBarcode.trim() || null,
        caseBarcode: formData.caseBarcode.trim() || null,
        supplierCode: formData.supplierCode.trim() || null,
        unitsPerCase,
        casePurchasePrice: formData.casePurchasePrice
          ? parseFloat(formData.casePurchasePrice)
          : null,
        unitWeight: formData.unitWeight ? parseFloat(formData.unitWeight) : null,
        caseWeight: formData.caseWeight ? parseFloat(formData.caseWeight) : null,
        casesPerLayer: formData.casesPerLayer
          ? parseInt(formData.casesPerLayer, 10)
          : null,
        layersPerPallet: formData.layersPerPallet
          ? parseInt(formData.layersPerPallet, 10)
          : null,
        brandId: formData.brandId === 'none' ? null : formData.brandId,
        status: formData.status,
        categoryId:
          formData.categoryId && formData.categoryId !== 'none'
            ? formData.categoryId
            : null,
        supplierId: formData.supplierId === 'none' ? null : formData.supplierId,
        purchasePrice:
          formData.status === 'UNMANAGED' && formData.purchasePrice.trim() === ''
            ? 0
            : parseFloat(formData.purchasePrice),
        sellPrice: parseFloat(formData.sellPrice),
        targetMarginPercent: parseFloat(formData.targetMarginPercent),
        reorderLeadDays: parseInt(formData.reorderLeadDays, 10),
        isActive: statusToIsActive(formData.status),
      };

      if (!payload.name) throw new Error('Name is required');
      if (!payload.sku) throw new Error('SKU is required');
      if (formData.status !== 'UNMANAGED') {
        if (isNaN(payload.purchasePrice) || payload.purchasePrice <= 0) {
          throw new Error('Purchase price must be positive');
        }
      } else if (!Number.isFinite(payload.purchasePrice) || payload.purchasePrice < 0) {
        throw new Error('Purchase price cannot be negative');
      }
      if (isNaN(payload.sellPrice) || payload.sellPrice <= 0) {
        throw new Error('Sell price (RRP) must be positive');
      }

      const url = isEdit ? `/api/v1/products/${id}` : '/api/v1/products';
      const method = isEdit ? 'PATCH' : 'POST';

      const res = await apiFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        let data: unknown;
        try {
          data = await res.json();
        } catch {
          throw new Error('Failed to save product');
        }
        throw new Error(formatApiErrorBody(data));
      }

      const saved = await res.json();
      navigate(`/inventory/products/${saved.id}`, productsListNav);
    } catch (err: unknown) {
      // Dialog rather than the top-of-page banner: on a long form the banner
      // can be scrolled out of view, making a failed save look like a no-op.
      setSaveError(err instanceof Error ? err.message : 'Failed to save product');
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center">
        <p className="text-sm text-muted-foreground">Loading product…</p>
      </div>
    );
  }

  return (
    <div className="pb-6">
      <div
        className={cn(
          'sticky top-0 z-10 mb-6 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80',
          pageBleedXClass
        )}
      >
        <div
          className={cn(
            'mx-auto flex w-full min-w-0 max-w-6xl flex-col gap-4 sm:flex-row sm:items-center sm:justify-between',
            PAGE_GUTTERS,
            'py-4'
          )}
        >
          <div className="flex items-start gap-3 min-w-0">
            <Button
              variant="ghost"
              size="icon"
              className="shrink-0 mt-0.5"
              onClick={navigateBack}
              aria-label="Back"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div className="min-w-0">
              <p className="text-xs text-muted-foreground mb-1">
                {isEdit ? (
                  <Link to={productsListHref} state={productsListNav.state} className="hover:underline">
                    Products
                  </Link>
                ) : (
                  'Products'
                )}
                {' / '}
                {isEdit ? 'Edit' : 'New product'}
              </p>
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="text-xl font-semibold tracking-tight truncate">
                  {isEdit && formData.name ? formData.name : isEdit ? 'Edit product' : 'Create product'}
                </h1>
                {formData.sku && (
                  <code className="text-xs font-mono bg-muted px-2 py-0.5 rounded">
                    {formData.sku}
                  </code>
                )}
                {isEdit && <ProductStatusBadge status={formData.status} />}
              </div>
            </div>
          </div>
          <div className="flex gap-2 sm:shrink-0">
            <Button
              type="button"
              variant="outline"
              onClick={navigateBack}
            >
              Cancel
            </Button>
            <Button type="submit" form="product-form" disabled={saving}>
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </div>
      </div>

      <Dialog open={saveError !== null} onOpenChange={(open) => !open && setSaveError(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Product not saved</DialogTitle>
            <DialogDescription className="whitespace-pre-line pt-2">
              {saveError}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" onClick={() => setSaveError(null)}>
              OK
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="mx-auto w-full max-w-6xl">
        {error && (
          <div
            role="alert"
            className="rounded-lg border border-destructive/30 bg-destructive/10 text-destructive px-4 py-3 mb-6 text-sm whitespace-pre-line"
          >
            {error}
          </div>
        )}

        <form id="product-form" onSubmit={handleSubmit}>
          <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(280px,320px)] lg:items-start">
            <div className="space-y-6 min-w-0">
              <FormSection
                title="Product details"
                description="Core catalog fields used across lists, pick lists, and receipts."
              >
                <div className="grid gap-4 sm:grid-cols-2">
                  <FormField id="name" label="Product name" required className="sm:col-span-2">
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => updateField('name', e.target.value)}
                      placeholder="e.g. Red Bull 250ml"
                      required
                    />
                  </FormField>
                  <FormField
                    id="sku"
                    label="SKU"
                    hint={isEdit ? 'SKU cannot be changed after creation.' : 'Uppercase letters, numbers, and hyphens.'}
                    required
                  >
                    <Input
                      id="sku"
                      value={formData.sku}
                      onChange={(e) => updateField('sku', e.target.value.toUpperCase())}
                      placeholder="ABC-123"
                      pattern="^[A-Z0-9-]+$"
                      disabled={isEdit}
                      className="font-mono text-sm"
                      required
                    />
                  </FormField>
                  <FormField id="categoryId" label="Category">
                    <Select
                      value={formData.categoryId || 'none'}
                      onValueChange={(v) => updateField('categoryId', v)}
                    >
                      <SelectTrigger id="categoryId">
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No category</SelectItem>
                        {categories.map((cat) => (
                          <SelectItem key={cat.id} value={cat.id}>
                            {cat.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormField>
                  <FormField id="supplierId" label="Supplier">
                    <Select
                      value={formData.supplierId || 'none'}
                      onValueChange={(v) => updateField('supplierId', v)}
                    >
                      <SelectTrigger id="supplierId">
                        <SelectValue placeholder="Select supplier" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No supplier</SelectItem>
                        {suppliers.map((sup) => (
                          <SelectItem key={sup.id} value={sup.id}>
                            {sup.name} ({sup.code})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormField>
                  <FormField id="brandId" label="Brand">
                    <Select
                      value={formData.brandId}
                      onValueChange={(v) => updateField('brandId', v)}
                    >
                      <SelectTrigger id="brandId">
                        <SelectValue placeholder="Select brand" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No brand</SelectItem>
                        {brands.map((b) => (
                          <SelectItem key={b.id} value={b.id}>
                            {b.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormField>
                  <FormField
                    id="status"
                    label="Status"
                    hint="Unmanaged hides the product everywhere until you set a Cloud status again. Cloud sync will not overwrite Unmanaged."
                  >
                    <Select
                      value={formData.status}
                      onValueChange={(v) => updateField('status', v as ProductStatus)}
                    >
                      <SelectTrigger id="status">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ACTIVE">Active</SelectItem>
                        <SelectItem value="DORMANT">Dormant</SelectItem>
                        <SelectItem value="DELISTED">Delisted</SelectItem>
                        <SelectItem value="UNMANAGED">Unmanaged</SelectItem>
                      </SelectContent>
                    </Select>
                  </FormField>
                </div>
              </FormSection>

              <FormSection
                title="Pack configuration"
                description="Used for goods receipt case counting and pick weight estimates."
              >
                <div className="grid gap-4 sm:grid-cols-2">
                  <FormField
                    id="unitsPerCase"
                    label="Units per case"
                    required
                    hint="Required for warehouse case counting."
                  >
                    <Input
                      id="unitsPerCase"
                      type="number"
                      min={1}
                      value={formData.unitsPerCase}
                      onChange={(e) => {
                        setFormData((prev) =>
                          applyCasePackPricingUpdate(prev, 'unitsPerCase', e.target.value)
                        );
                        setUnitsPerCaseError(null);
                      }}
                      aria-invalid={Boolean(unitsPerCaseError)}
                      className={cn(unitsPerCaseError && 'border-destructive')}
                      required
                    />
                    {unitsPerCaseError && (
                      <p className="text-xs text-destructive">{unitsPerCaseError}</p>
                    )}
                  </FormField>
                  <FormField id="casePurchasePrice" label="Case purchase price (£)">
                    <Input
                      id="casePurchasePrice"
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.casePurchasePrice}
                      onChange={(e) =>
                        setFormData((prev) =>
                          applyCasePackPricingUpdate(prev, 'casePurchasePrice', e.target.value)
                        )
                      }
                      placeholder="0.00"
                    />
                  </FormField>
                  <FormField id="unitWeight" label="Unit weight (g)">
                    <Input
                      id="unitWeight"
                      type="number"
                      step="0.001"
                      min="0"
                      value={formData.unitWeight}
                      onChange={(e) => updateField('unitWeight', e.target.value)}
                    />
                  </FormField>
                  <FormField id="caseWeight" label="Case weight (kg)">
                    <Input
                      id="caseWeight"
                      type="number"
                      step="0.001"
                      min="0"
                      value={formData.caseWeight}
                      onChange={(e) => updateField('caseWeight', e.target.value)}
                    />
                  </FormField>
                  <FormField id="casesPerLayer" label="Cases per layer">
                    <Input
                      id="casesPerLayer"
                      type="number"
                      min="1"
                      value={formData.casesPerLayer}
                      onChange={(e) => updateField('casesPerLayer', e.target.value)}
                    />
                  </FormField>
                  <FormField id="layersPerPallet" label="Layers per pallet">
                    <Input
                      id="layersPerPallet"
                      type="number"
                      min="1"
                      value={formData.layersPerPallet}
                      onChange={(e) => updateField('layersPerPallet', e.target.value)}
                    />
                  </FormField>
                </div>
                {palletCases != null && (
                  <p className="text-sm text-muted-foreground rounded-md bg-muted/50 px-3 py-2">
                    Derived pallet size: <span className="font-medium text-foreground">{palletCases} cases</span>
                  </p>
                )}
              </FormSection>

              <FormSection
                title="Identifiers"
                description="Barcodes and wholesaler codes for picking and invoice matching."
              >
                <div className="grid gap-4 sm:grid-cols-2">
                  <FormField
                    id="unitBarcode"
                    label="Unit barcode (EAN-13)"
                    className="sm:col-span-2"
                  >
                    <BarcodeScanInput
                      id="unitBarcode"
                      value={formData.unitBarcode}
                      onChange={(value) => updateField('unitBarcode', value)}
                      placeholder="9002490100103"
                      scanTitle="Scan unit barcode"
                      scanDescription="Scan the EAN-13 on a single product unit."
                    />
                  </FormField>
                  <FormField id="caseBarcode" label="Case barcode (ITF-14)">
                    <BarcodeScanInput
                      id="caseBarcode"
                      value={formData.caseBarcode}
                      onChange={(value) => updateField('caseBarcode', value)}
                      placeholder="15000249010017"
                      scanTitle="Scan case barcode"
                      scanDescription="Scan the ITF-14 or case-level barcode."
                    />
                  </FormField>
                  <FormField id="supplierCode" label="Supplier code">
                    <Input
                      id="supplierCode"
                      value={formData.supplierCode}
                      onChange={(e) => updateField('supplierCode', e.target.value)}
                      placeholder="Wholesaler reference"
                    />
                  </FormField>
                </div>
                {isEdit && (
                  <div className="flex flex-col gap-2 pt-1 sm:flex-row sm:items-center sm:justify-between">
                    <p className="text-xs text-muted-foreground">
                      Matches this product to Occa by unit barcode and fills empty identifier, pack,
                      weight, image, and price fields.
                    </p>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="shrink-0"
                      disabled={occaEnriching || !formData.unitBarcode.trim()}
                      onClick={() => void handleEnrichFromOcca()}
                    >
                      <Download className="mr-1.5 h-4 w-4" />
                      {occaEnriching ? 'Fetching from Occa…' : 'Enrich from Occa'}
                    </Button>
                  </div>
                )}
                {occaEnrichMessage && (
                  <p
                    className={cn(
                      'text-sm',
                      occaEnrichMessage.includes('failed') ||
                        occaEnrichMessage.includes('Add a unit barcode')
                        ? 'text-destructive'
                        : 'text-muted-foreground'
                    )}
                  >
                    {occaEnrichMessage}
                  </p>
                )}
              </FormSection>

              <FormSection
                title="Pricing"
                description="Unit costs and RRP. SKU margin excludes venue commission."
              >
                <div className="grid gap-4 sm:grid-cols-2">
                  <FormField
                    id="purchasePrice"
                    label="Unit purchase price (£)"
                    required={!isUnmanaged}
                    hint={
                      isUnmanaged
                        ? 'Optional for unmanaged products — leave blank if the cost is unknown.'
                        : 'Auto-filled from case purchase price ÷ units per case when both are set.'
                    }
                  >
                    <Input
                      id="purchasePrice"
                      type="number"
                      step="0.01"
                      min={isUnmanaged ? '0' : '0.01'}
                      value={formData.purchasePrice}
                      onChange={(e) => updateField('purchasePrice', e.target.value)}
                      placeholder="0.00"
                      required={!isUnmanaged}
                    />
                  </FormField>
                  <FormField id="sellPrice" label="Sell price / RRP (£)" required>
                    <Input
                      id="sellPrice"
                      type="number"
                      step="0.01"
                      min="0.01"
                      value={formData.sellPrice}
                      onChange={(e) => updateField('sellPrice', e.target.value)}
                      placeholder="0.00"
                      required
                    />
                  </FormField>
                  <FormField
                    id="targetMarginPercent"
                    label="Target margin (%)"
                    hint="Operating margin target including commission."
                  >
                    <Input
                      id="targetMarginPercent"
                      type="number"
                      step="1"
                      min="0"
                      max="100"
                      value={formData.targetMarginPercent}
                      onChange={(e) => updateField('targetMarginPercent', e.target.value)}
                    />
                  </FormField>
                  <FormField id="reorderLeadDays" label="Reorder lead time (days)">
                    <Input
                      id="reorderLeadDays"
                      type="number"
                      step="1"
                      min="1"
                      value={formData.reorderLeadDays}
                      onChange={(e) => updateField('reorderLeadDays', e.target.value)}
                    />
                  </FormField>
                </div>

                {showMarginPreview && (
                  <>
                    <Separator />
                    <div className="lg:hidden">
                      <MarginCalculator
                        purchasePrice={purchasePrice}
                        sellPrice={sellPrice}
                        targetMarginPercent={targetMargin}
                        supplierName={selectedSupplierName}
                      />
                    </div>
                  </>
                )}
              </FormSection>
            </div>

            <aside className="space-y-4 lg:sticky lg:top-[5.5rem]">
              {showMarginPreview ? (
                <div className="hidden lg:block">
                  <MarginCalculator
                    purchasePrice={purchasePrice}
                    sellPrice={sellPrice}
                    targetMarginPercent={targetMargin}
                    supplierName={selectedSupplierName}
                  />
                </div>
              ) : (
                <Card className="hidden lg:block">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">Margin preview</CardTitle>
                    <CardDescription>
                      Enter unit purchase and sell prices to see SKU margin.
                    </CardDescription>
                  </CardHeader>
                </Card>
              )}

              <Card className="lg:hidden">
                <CardContent className="pt-6 flex gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    className="flex-1"
                    onClick={navigateBack}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" className="flex-1" disabled={saving}>
                    {saving ? 'Saving…' : 'Save'}
                  </Button>
                </CardContent>
              </Card>
            </aside>
          </div>
        </form>
      </div>
    </div>
  );
}
